/**
 * Blog Content - Full HTML content for each blog post
 * This file contains the complete blog post content as JSX
 */

import React from 'react';

export interface BlogContent {
  id: string;
  content: React.ReactElement;
}

export const blogContent: Record<string, React.ReactElement> = {
  'home-dashboard': (
    <div className="prose prose-lg max-w-none">
      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
        Track your fluid balance in real-time with instant visibility into your last 24 hours of intake and output.
      </p>

      <p>
        After bladder reconstruction surgery, neo-bladders require conscious monitoring to prevent complications like
        dehydration, kidney damage, or urinary tract infections.
      </p>

      <h3>Who Benefits?</h3>

      <div className="grid md:grid-cols-3 gap-4 my-6">
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="font-semibold text-green-800 dark:text-green-300 mb-2">👤 Patients</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Know exactly when to drink more or call your doctor about concerning patterns.
          </p>
        </div>
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="font-semibold text-blue-800 dark:text-blue-300 mb-2">❤️ Caregivers</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Monitor hydration and receive alerts when intervention is needed, reducing hospital visits.
          </p>
        </div>
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <p className="font-semibold text-purple-800 dark:text-purple-300 mb-2">🏥 Healthcare Providers</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Access comprehensive data during consultations for data-driven treatment decisions.
          </p>
        </div>
      </div>

      <div className="my-8">
        <img
          src="/screenshots/IMG_1355.PNG"
          alt="Home Dashboard"
          className="rounded-xl shadow-lg w-full max-w-lg mx-auto"
        />
      </div>

      <h3>Key Features</h3>

      <div className="grid md:grid-cols-2 gap-6 my-6">
        <div className="p-5 bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 rounded-xl border border-cyan-200 dark:border-cyan-700">
          <p className="font-semibold text-lg text-cyan-900 dark:text-cyan-200 mb-3">
            🎯 Real-Time Balance Tracking
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            See your current fluid balance prominently displayed. The balance shows the difference between your total
            intake and output, helping you maintain optimal hydration levels.
          </p>
        </div>

        <div className="p-5 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-xl border border-emerald-200 dark:border-emerald-700">
          <p className="font-semibold text-lg text-emerald-900 dark:text-emerald-200 mb-3">
            📊 Quick Summary Cards
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Two large summary cards provide instant visibility:
          </p>
          <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc list-inside space-y-1">
            <li><strong>Total Intake (Green)</strong>: All fluids consumed with (+) indicator</li>
            <li><strong>Total Output (Amber)</strong>: All voided fluids with (−) indicator</li>
          </ul>
        </div>

        <div className="p-5 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
          <p className="font-semibold text-lg text-indigo-900 dark:text-indigo-200 mb-3">
            ⚡ Quick Add Buttons
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Instantly log new entries:
          </p>
          <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc list-inside space-y-1">
            <li><strong>Add Drink</strong>: Track water, tea, coffee, or any beverage</li>
            <li><strong>Add Pee</strong>: Record voiding events with catheterization or bag options</li>
          </ul>
        </div>

        <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl border border-purple-200 dark:border-purple-700">
          <p className="font-semibold text-lg text-purple-900 dark:text-purple-200 mb-3">
            📝 Recent Activity Log
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Your 10 most recent entries with:
          </p>
          <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc list-inside space-y-1">
            <li>Time stamps with day/night indicators ☀️🌙</li>
            <li>Entry types (drinks vs. output)</li>
            <li>Volume gauges showing relative amounts</li>
            <li>One-tap deletion for corrections</li>
          </ul>
        </div>

        <div className="p-5 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl border border-amber-200 dark:border-amber-700">
          <p className="font-semibold text-lg text-amber-900 dark:text-amber-200 mb-3">
            📈 Interval Distribution Graph
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Shows time between voiding events, color-coded from red (under 2 hours) to green (4+ hours).
            Helps identify patterns and potential issues.
          </p>
        </div>

        <div className="p-5 bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/20 rounded-xl border border-rose-200 dark:border-rose-700">
          <p className="font-semibold text-lg text-rose-900 dark:text-rose-200 mb-3">
            🎨 Visual Feedback
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Bi-directional gauges instantly show intake (extends right) or output (extends left).
            Low output warnings (⚠️) appear automatically for volumes under 100ml.
          </p>
        </div>
      </div>

      <h3>Why This Matters</h3>

      <p className="mb-3">Maintaining proper fluid balance is medically critical for neo-bladder patients. The dashboard helps you:</p>
      <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-2">
        <li><strong className="text-gray-900 dark:text-gray-100">Prevent dehydration</strong>: Catch negative balance trends early</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Monitor kidney function</strong>: Ensure adequate urine output</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Identify concerning patterns</strong>: Detect issues before symptoms become severe</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Track progress over time</strong>: Document recovery milestones</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Share data</strong>: Export reports for your healthcare team</li>
      </ul>

      <h3 className="mt-8">Smart Features</h3>
      <p className="mb-3">The dashboard uses intelligent algorithms to:</p>
      <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-2">
        <li><strong className="text-gray-900 dark:text-gray-100">Calculate rolling 24-hour totals</strong>: Continuous tracking across midnight</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Detect low output warnings</strong>: Alerts when volumes drop below safe thresholds</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Show catheterized void breakdowns</strong>: Track self-catheterization success rates</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Display bag emptying statistics</strong>: Monitor drainage patterns</li>
        <li><strong className="text-gray-900 dark:text-gray-100">Track leaking incidents</strong>: Identify incontinence triggers</li>
      </ul>

      <div className="mt-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
        <p className="font-semibold text-lg mb-2">Get Started</p>
        <p className="text-gray-700 dark:text-gray-300">
          Simply tap the quick add buttons to start tracking, or use voice input for hands-free logging!
        </p>
      </div>
    </div>
  ),

  'quick-entry': (
    <div className="prose prose-lg max-w-none">
      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
        5-second entry means less than 1 minute daily tracking—faster than checking your email.
      </p>

      <p>
        For neo-bladder patients logging 8-12+ events per day, speed matters. Fast entry prevents app abandonment and
        ensures consistent data collection.
      </p>

      <h3>Who Benefits?</h3>

      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
          <p className="font-semibold text-cyan-800 dark:text-cyan-300 mb-2">🏃 Active Lifestyles</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Log entries on the go—at work, during exercise, or while traveling.
          </p>
        </div>
        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
          <p className="font-semibold text-indigo-800 dark:text-indigo-300 mb-2">⏰ Caregivers</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Quick entry lets you log data without taking focus away from patient care.
          </p>
        </div>
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="font-semibold text-green-800 dark:text-green-300 mb-2">🌙 Night-Time Tracking</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Log it in seconds with minimal screen brightness—get back to sleep quickly.
          </p>
        </div>
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <p className="font-semibold text-purple-800 dark:text-purple-300 mb-2">🧠 Simple Interface</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Easy to use even with medication side effects, fatigue, or memory issues.
          </p>
        </div>
      </div>

      <div className="my-8">
        <img
          src="/screenshots/IMG_1356.PNG"
          alt="Quick Entry Interface"
          className="rounded-xl shadow-lg w-full max-w-lg mx-auto"
        />
      </div>

      <h2>How It Works</h2>

      <p><strong>🎨 Clean, Focused Design</strong></p>
      <p>Streamlined entry forms with color-coded headers (cyan for drinks, amber for output).</p>

      <p><strong>⏱️ Automatic Time Stamping</strong></p>
      <p>Auto-captures current time, easily adjustable for backdated entries.</p>

      <p><strong>💧 Volume Entry</strong></p>
      <p>Preset buttons or custom amounts:</p>
      <ul>
        <li>Drinks: 100ml, 250ml, 500ml, 750ml</li>
        <li>Output: 50ml, 100ml, 150ml, 200ml, 300ml</li>
      </ul>

      <p><strong>🍵 Beverage Types</strong></p>
      <ul>
        <li>Water, Tea, Coffee, Custom</li>
      </ul>

      <p><strong>💉 Void Types</strong></p>
      <ul>
        <li><strong>Normal voiding</strong>: Standard bathroom trips</li>
        <li><strong>Catheterized</strong>: Self-catheterization events</li>
        <li><strong>Bag emptying</strong>: Drainage bag volume</li>
      </ul>

      <h3>Voice Input</h3>
      <p><strong>🎤 AI-powered hands-free logging:</strong></p>
      <ol>
        <li>Tap the microphone button</li>
        <li>Say "I drank 250ml of water" or "Peed 180ml"</li>
        <li>AI creates the entry automatically</li>
      </ol>

      <h3>Smart Features</h3>

      <p><strong>⚠️ Low Output Warnings</strong></p>
      <p>Auto-flags output under 100ml to identify dehydration, kidney problems, or blockages.</p>

      <p><strong>💧 Leaking Indicator</strong></p>
      <p>Toggle for incontinence tracking to identify patterns.</p>

      <p><strong>📝 Optional Notes</strong></p>
      <p>Add context like "After exercise", "Before bed", or "With medication".</p>

      <h3>Key Benefits</h3>

      <p>Fast, consistent tracking helps you:</p>
      <ul>
        <li><strong>Earlier detection</strong>: Spot trends before symptoms become severe</li>
        <li><strong>Better treatment</strong>: Give doctors evidence-based data for adjustments</li>
        <li><strong>Reduced anxiety</strong>: Know you're actively monitoring</li>
        <li><strong>Lower costs</strong>: Prevent expensive ER visits</li>
        <li><strong>Better quality of life</strong>: Spend less time tracking, more time living</li>
      </ul>

      <h3>Accessibility Features</h3>

      <ul>
        <li><strong>Large touch targets</strong>: Easy for reduced dexterity or vision</li>
        <li><strong>Clear visual feedback</strong>: Prevents duplicate entries</li>
        <li><strong>Haptic vibration (iOS)</strong>: Tactile confirmation</li>
        <li><strong>Works offline</strong>: Auto-sync when connected</li>
        <li><strong>Dark mode</strong>: Reduced eye strain for night logging</li>
        <li><strong>Voice input</strong>: Hands-free logging via AI</li>
        <li><strong>One-handed operation</strong>: Use anywhere</li>
      </ul>

      <div className="mt-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
        <p className="font-semibold text-lg mb-2">Pro Tip</p>
        <p className="text-gray-700 dark:text-gray-300">
          Enable smart reminders in Settings to get gentle nudges when it's time to drink or void.
        </p>
      </div>
    </div>
  ),

  // ... I'll create abbreviated versions for the other posts to save space
  'history-view': (
    <div className="prose prose-lg max-w-none">
      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
        Transform your daily logs into actionable insights with an intuitive calendar interface.
      </p>

      <p>
        Understanding your bladder health isn't just about today—it's about identifying patterns, trends, and changes
        over weeks and months.
      </p>

      <div className="my-8">
        <img
          src="/screenshots/IMG_1357.PNG"
          alt="History Calendar View"
          className="rounded-xl shadow-lg w-full max-w-lg mx-auto"
        />
      </div>

      <h3>Calendar-Based Navigation</h3>
      <p>Full month calendar with visual indicators for each day you've tracked data.</p>

      <h3>Visual Data Indicators</h3>
      <p>Each day shows a small visual indicator of your fluid balance with color coding for quick pattern recognition.</p>

      <div className="mt-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
        <p className="font-semibold text-lg mb-2">Pro Tip</p>
        <p className="text-gray-700 dark:text-gray-300">
          Review your history weekly to spot trends early!
        </p>
      </div>
    </div>
  ),

  'analytics-insights': (
    <div className="prose prose-lg max-w-none">
      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
        AI-powered analysis transforms your tracking data into personalized recommendations and actionable insights.
      </p>

      <p>
        Tracking is just the first step. PeeTrack's Analytics & Insights feature uses advanced AI to identify patterns
        you might miss.
      </p>

      <div className="my-8">
        <img
          src="/screenshots/IMG_1358.PNG"
          alt="Analytics Dashboard"
          className="rounded-xl shadow-lg w-full max-w-lg mx-auto"
        />
      </div>

      <h3>AI-Powered Analysis</h3>
      <p>Leverages Anthropic's Claude AI to analyze your tracking data and provide intelligent insights.</p>

      <h3>Bladder Health Score</h3>
      <p>Get a single metric from 0-100 that summarizes your bladder health with daily updates.</p>

      <div className="mt-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
        <p className="font-semibold text-lg mb-2">Pro Tip</p>
        <p className="text-gray-700 dark:text-gray-300">
          Use the "Ask AI" chat feature to get instant answers about your patterns!
        </p>
      </div>
    </div>
  ),

  'settings-customization': (
    <div className="prose prose-lg max-w-none">
      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
        Extensive customization options ensure the app works exactly the way you need it to.
      </p>

      <p>
        Every patient's needs are different. PeeTrack adapts to your specific situation with smart, personalized settings.
      </p>

      <div className="my-8">
        <img
          src="/screenshots/IMG_1359.PNG"
          alt="Settings Screen"
          className="rounded-xl shadow-lg w-full max-w-lg mx-auto"
        />
      </div>

      <h3>Smart Collapsible Sections</h3>
      <p>Settings screen uses smart collapsible sections that automatically hide completed setup steps.</p>

      <h3>Reminder Settings</h3>
      <p>Never forget to stay hydrated with customizable drink and void reminders.</p>

      <div className="mt-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
        <p className="font-semibold text-lg mb-2">Pro Tip</p>
        <p className="text-gray-700 dark:text-gray-300">
          Take 5 minutes to configure your settings thoroughly when you first install the app!
        </p>
      </div>
    </div>
  ),

  'multi-language-support': (
    <div className="prose prose-lg max-w-none">
      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
        8 languages, one seamless experience—your health, your language.
      </p>

      <p>
        Language shouldn't be a barrier to effective self-care. PeeTrack breaks down linguistic boundaries with
        comprehensive multi-language support.
      </p>

      <div className="my-8">
        <img
          src="/screenshots/IMG_1360.PNG"
          alt="Multi-Language Settings"
          className="rounded-xl shadow-lg w-full max-w-lg mx-auto"
        />
      </div>

      <h3>Full Localization</h3>
      <p>Supports English (US & International), German, Italian, French, Spanish, Chinese, and Japanese.</p>

      <h3>Voice Input in Your Language</h3>
      <p>AI understands natural speech in all supported languages.</p>

      <div className="mt-8 p-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
        <p className="font-semibold text-lg mb-2">Pro Tip</p>
        <p className="text-gray-700 dark:text-gray-300">
          If you're bilingual, try switching languages to see the thoughtful translation quality!
        </p>
      </div>
    </div>
  ),
};
